import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IMAGES } from "../data/site";
import { MaskedLine, Reveal, Eyebrow } from "../components/Reveal";
import { Marquee } from "../components/Marquee";

const VALUES = [
  { n: "01", title: "Spec before price", text: "We would rather lose a deal than ship off-spec product. Every quote references the test methods your QC team will use." },
  { n: "02", title: "One desk, full chain", text: "Sourcing, inspection, freight, insurance and documentation run through a single accountable trading desk." },
  { n: "03", title: "Straight talk, every quote", text: "Availability, lead time and landed cost stated plainly in every offer — no surprises between proforma and bill of lading." },
  { n: "04", title: "Compliance as standard", text: "REACH-conscious low-PCA grades, GHS-aligned SDS documentation, and third-party inspection on request." },
];

export default function About() {
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 500], [0, 140]);

  return (
    <>
      <section data-testid="about-hero" className="relative isolate flex min-h-[70vh] items-end overflow-hidden noise-surface">
        <motion.div style={{ y: yBg }} className="absolute inset-0 -z-10">
          <img src={IMAGES.sunset} alt="Refinery silhouette at sunset" className="h-[115%] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A101D] via-[#0A101D]/70 to-[#0A101D]/40" />
        </motion.div>
        <div className="mx-auto w-full max-w-7xl px-5 pb-16 pt-40 lg:px-8">
          <Eyebrow data-testid="about-eyebrow">About Terra Bloom</Eyebrow>
          <h1 className="mt-5 font-display text-5xl font-bold uppercase leading-[0.98] text-white sm:text-7xl">
            <MaskedLine delay={0.15}>Built in Dubai.</MaskedLine>
            <MaskedLine delay={0.3} innerClassName="text-amber-500">Trusted worldwide.</MaskedLine>
          </h1>
        </div>
      </section>

      <section data-testid="about-story" className="bg-white py-24 md:py-32">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 lg:grid-cols-[1fr_1.4fr] lg:px-8">
          <Reveal><Eyebrow>Our story</Eyebrow></Reveal>
          <div>
            <Reveal>
              <p className="font-display text-2xl font-medium leading-snug text-[#0E1526] sm:text-3xl">
                Terra Bloom DWC LLC is an international trading company built on more than 20 years of experience
                in petroleum products, rubber chemicals and industrial raw materials.
              </p>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-8 text-base leading-relaxed text-slate-700">
                Our global procurement and sales network connects trusted suppliers with manufacturers, blenders
                and distributors across Asia, the Middle East, Africa and Europe. We specialize in rubber process
                oils, base oils, paraffin waxes, lubricants, rubber chemicals, steel products and related
                products, with a strong focus on competitive sourcing, timely delivery and reliable execution.
              </p>
              <p className="mt-4 text-base leading-relaxed text-slate-600">
                Our mission is simple: make premium petroleum products as dependable for a tyre plant in Nairobi or
                a lube blender in Jakarta as they are for a refinery's next-door neighbor.
              </p>
              <p className="mt-4 text-base leading-relaxed text-slate-600">
                From sourcing and commercial negotiation to documentation, logistics and final delivery, we manage
                every transaction with experience, transparency and attention to execution.
              </p>
              <p
                data-testid="about-story-signature"
                className="mt-10 border-t border-black/10 pt-6 font-mono text-xs uppercase tracking-[0.25em] text-amber-600"
              >
                Global relationships. Deep market experience. Reliable execution.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee items={["Quality", "Sustainability", "Reliability", "Compliance", "Partnership"]} />

      <section data-testid="about-values" className="bg-[#F5F5F2] py-24 md:py-32 noise-surface">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><Eyebrow>How we work</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 max-w-2xl font-display text-4xl font-bold uppercase leading-tight text-[#0E1526] sm:text-5xl">
              Values written in chapters
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-px border border-black/10 bg-black/[0.08] sm:grid-cols-2">
            {VALUES.map((v, i) => (
              <Reveal key={v.n} delay={(i % 2) * 0.1} className="bg-white p-10 transition-colors hover:bg-[#F1F5F9]">
                <span className="font-mono text-sm text-amber-500">{v.n}.</span>
                <h3 className="mt-6 font-display text-2xl font-semibold uppercase tracking-wide text-[#0E1526]">{v.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{v.text}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/contact"
              data-testid="about-cta-button"
              className="group mt-14 inline-flex items-center gap-2 bg-amber-500 px-8 py-4 text-sm font-semibold text-[#0A101D] transition-colors hover:bg-amber-400"
            >
              Talk to the team <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
